import axios from 'axios';
import * as Sentry from '@sentry/vue';
import { getRuntimeEnv } from '@/constants/runtimeConfig';

/**
 * Best-effort client for survey-builder-glue's public translations endpoint
 * (`GET {SBG}/api/public/v1/translations/{locale}`).
 *
 * `axios` is used directly rather than `ApiService` on purpose:
 * - `ApiService` reports every failure to Sentry, but this call has a fallback
 *   (the bundled translations) and must not raise Sentry events;
 * - `HttpError.fromResponse` throws a `TypeError` on network errors/timeouts.
 */

export const REMOTE_TRANSLATIONS_TIMEOUT_MS = 10000;

export type RemoteTranslations = Record<string, string>;

export type RemoteTranslationsResult =
  | { ok: true; messages: RemoteTranslations }
  | { ok: false; messages: RemoteTranslations };

export const getSbgUrl = (): string =>
  getRuntimeEnv('VITE_SURVEY_BUILDER_GLUE_URL', import.meta.env.VITE_SURVEY_BUILDER_GLUE_URL).trim().replace(/\/+$/, '');

/** An empty `VITE_SURVEY_BUILDER_GLUE_URL` turns the feature off */
export const isRemoteTranslationsEnabled = (): boolean => getSbgUrl().length > 0;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

const getContentType = (headers: unknown): string => {
  if (!headers) return '';
  const h = headers as { get?: (name: string) => unknown } & Record<string, unknown>;
  const value = typeof h.get === 'function'
    ? h.get('content-type')
    : h['content-type'] ?? h['Content-Type'];
  return typeof value === 'string' ? value : '';
};

const failure = (locale: string, reason: string, extra: Record<string, unknown> = {}): RemoteTranslationsResult => {
  console.warn(`[translations] Could not load remote translations for "${locale}": ${reason}`);
  Sentry.addBreadcrumb({
    category: 'i18n',
    level: 'warning',
    message: 'Remote translations fetch failed',
    data: { locale, reason, ...extra },
  });
  return { ok: false, messages: {} };
};

/**
 * Fetches the translations SBG has for one locale.
 *
 * Never throws. On any failure it resolves with `{ ok: false, messages: {} }`,
 * logs a `console.warn` and leaves a Sentry breadcrumb (no `captureException`).
 * A `200` with `{}` is a success.
 */
export const fetchRemoteTranslations = async (locale: string): Promise<RemoteTranslationsResult> => {
  const baseUrl = getSbgUrl();
  if (!baseUrl) return { ok: false, messages: {} };

  const url = `${baseUrl}/api/public/v1/translations/${encodeURIComponent(locale)}`;

  let response;
  try {
    response = await axios.get(url, {
      timeout: REMOTE_TRANSLATIONS_TIMEOUT_MS,
      withCredentials: false,
      responseType: 'json',
      headers: { Accept: 'application/json' },
      // Handle every status ourselves instead of letting axios throw.
      validateStatus: () => true,
    });
  } catch (error) {
    const err = error as { code?: string; message?: string };
    return failure(locale, err?.code || err?.message || 'network error');
  }

  if (!response || response.status !== 200) {
    return failure(locale, `unexpected status ${response?.status}`, { status: response?.status });
  }

  const contentType = getContentType(response.headers);
  if (!contentType.toLowerCase().includes('json')) {
    return failure(locale, `unexpected content-type "${contentType}"`, { contentType });
  }

  const data: unknown = response.data;
  if (!isPlainObject(data)) {
    return failure(locale, 'response body is not a JSON object');
  }

  const messages: RemoteTranslations = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string' && value.trim().length > 0) {
      messages[key] = value;
    }
  }

  return { ok: true, messages };
};

